<template>
    <tbody>
        <tr class="section-title-row">
            <td
                :colspan="subject.session_list.length + 1"
            >
                {{$t("task_details:scope")}}
                
            </td>
        </tr>
        <tr v-if="editable" class="valuation-scope-task">
            <td></td>
            <td
                v-for="(session, session_idx) in subject.session_list"
                :key="'toggle-all-session-'+session_idx"
                class="text-center"
                style="vertical-align: middle;"
            >
                <input 
                    type="checkbox" 
                    :ref="'toggleAll-' + session_idx"
                    :checked="isAllChecked(session)"
                    @change="toggleAll(session, $event)"
                    :disabled="!hasEnabledItems()"
                    v-tooltip="$t('task_details:scope:toggle_all')"
                />
            </td>
        </tr>
        <tr v-for="(scope_item, idx) in getScopeData" :key="idx" class="valuation-scope-task">
            <td>
                {{scope_item.display_name}}
            </td>
            <td
                v-for="(session, session_idx) in subject.session_list" 
                :key="'scope-'+idx+'-session-'+session_idx"
                class="text-center"
                style="vertical-align: middle;"
                v-tooltip="scope_item.tooltip"
            >
                <input type="checkbox" 
                    v-if="editable"
                    v-model="session.scope[scope_item.name]"
                    :disabled="scope_item.disabled"
                />
                <div v-else>
                    <i 
                        class="fa"
                        :class="{
                            'fa fa-check-square-o': session.scope[scope_item.name],
                            'fa-square-o': !session.scope[scope_item.name],
                        }"
                    >
                    </i>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import countrymixin from '@/mixins/countrymixin';

export default {
    mixins: [ countrymixin ],
    model: {
        prop: 'subject',
        event: 'change'
    },
    props: {
        subject: {
            type: Object,
            default: () => {}
        },
        editable: {
            type: Boolean,
            required: false,
            default: true
        },
        permissions: {
            type: Object,
            required: true,
        },
    },
    updated() {
        this.updateIndeterminateStates();
    },
    mounted() {
        this.updateIndeterminateStates();
    },
    methods: {
        updateIndeterminateStates() {
            /*
            Manually set indeterminate property on DOM elements since Vue doesn't handle it properly
            */
            this.subject.session_list.forEach((session, index) => {
                const checkbox = this.$refs['toggleAll-' + index];
                if (checkbox && checkbox.length) {
                    checkbox[0].indeterminate = this.isIndeterminate(session);
                } else if (checkbox) {
                    checkbox.indeterminate = this.isIndeterminate(session);
                }
            });
        },
        countryScopeAvailable: function(task) {
            /*
            Returns true if scope is available for current country
            */
            return this.isCountryPluginLoaded && this.getCountryPlugin("AMAS_PROJECT_SCOPE")[task].available;
        },
        isAllChecked: function(session) {
            /*
            Returns true if all enabled scope items are checked for this session
            */
            const enabledItems = this.getScopeData.filter(item => !item.disabled);
            return enabledItems.length > 0 && enabledItems.every(item => session.scope[item.name]);
        },
        isIndeterminate: function(session) {
            /*
            Returns true if some but not all enabled scope items are checked
            */
            const enabledItems = this.getScopeData.filter(item => !item.disabled);
            const checkedItems = enabledItems.filter(item => session.scope[item.name]);
            return checkedItems.length > 0 && checkedItems.length < enabledItems.length;
        },
        hasEnabledItems: function() {
            /*
            Returns true if there are any enabled scope items
            */
            return this.getScopeData.some(item => !item.disabled);
        },
        toggleAll: function(session, event) {
            /*
            Toggle all enabled scope items for the given session
            */
            const shouldCheck = event.target.checked;
            const enabledItems = this.getScopeData.filter(item => !item.disabled);
            
            enabledItems.forEach(item => {
                this.$set(session.scope, item.name, shouldCheck);
            });
            
            // Emit change event to notify parent component
            this.$emit("change", this.subject);
            
            // Update indeterminate states after change
            this.$nextTick(() => {
                this.updateIndeterminateStates();
            });
        },
    },
    computed: {
        getScopeData: function() {
            /*
            Returns scope data for easier loop display
            */
            const scope = [];
            if (this.countryScopeAvailable("legal_status") && this.permissions.legalStatus)
                scope.push({
                    display_name: this.$t("task_details:scope:legal_status"),
                    name: "legal_status",
                });
            if (this.countryScopeAvailable("administrative_acts") && this.permissions.administrativeActs)
                scope.push({
                    display_name: this.$t("task_details:scope:administrative_acts"),
                    name: "administrative_acts",
                });
            if (this.countryScopeAvailable("public_road_access") && this.permissions.publicRoadAccess)
                scope.push({
                    display_name: this.$t("task_details:scope:public_road_access"),
                    name: "public_road_access",
                });
            if (this.countryScopeAvailable("comparable_transactions") && this.permissions.comparableTransactions)
                scope.push({
                    display_name: this.$t("task_details:scope:comparable_transactions"),
                    name: "comparable_transactions",
                    disabled: !this.permissions.accessExternalTransactions,
                    tooltip: !this.permissions.accessExternalTransactions ? this.$t("comparable:enekretnine:info") : null,
                });
            if (this.countryScopeAvailable("cost_method") && this.permissions.costMethod)
                scope.push({
                    display_name: this.$t("task_details:scope:cost_method"),
                    name: "cost_method",
                    disabled: !this.permissions.accessExternalTransactions,
                    tooltip: !this.permissions.accessExternalTransactions ? this.$t("comparable:enekretnine:info") : null,
                });
            if (this.countryScopeAvailable("prudent_value") && this.permissions.prudentValue)
                scope.push({
                    display_name: this.$t("task_details:scope:prudent_value"),
                    name: "prudent_value",
                    disabled: !this.permissions.accessExternalTransactions,
                    tooltip: !this.permissions.accessExternalTransactions ? this.$t("comparable:enekretnine:info") : null,
                });
            if (this.countryScopeAvailable("comparable_adverts") && this.permissions.comparableAdverts)
                scope.push({
                    display_name: this.$t("task_details:scope:comparable_adverts"),
                    name: "comparable_adverts",
                });
            if (this.countryScopeAvailable("income_based_approach") && this.permissions.incomeBasedApproach)
                scope.push({
                    display_name: this.$t("task_details:scope:income_based_approach"),
                    name: "income_based_approach",
                });
            if (this.countryScopeAvailable("esg_report") && this.permissions.esgReport)
                scope.push({
                    display_name: "ESG",
                    name: "esg_report",
                });
            if (this.countryScopeAvailable("region_analytics_dashboard") && this.permissions.marketAnalysis)
                scope.push({
                    display_name: this.$t("task_details:scope:region_analytics_dashboard"),
                    name: "region_analytics_dashboard",
                });
            if (this.countryScopeAvailable("document_cadastral_plan") && this.permissions.documentCadastralPlan)
                scope.push({
                    display_name: this.$t("task_details:scope:cadastral_plan"),
                    name: "document_cadastral_plan",
                });
            if (this.countryScopeAvailable("document_possession_sheet") && this.permissions.documentPossessionSheet)
                scope.push({
                    display_name: this.$t("task_details:scope:possession_sheet"),
                    name: "document_possession_sheet",
                });
            return scope;
        },
    },
}
</script>
